import { useState } from "react";
import { Flag, Mail, RefreshCw, Settings } from "lucide-react";
import { toast } from "sonner";

import AdminLayout from "@/components/admin/AdminLayout";
import AppSettingsTable from "@/components/admin/settings/AppSettingsTable";
import EmailTemplateAccordion from "@/components/admin/settings/EmailTemplateAccordion";
import FeatureFlagTable from "@/components/admin/settings/FeatureFlagTable";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAppSettings } from "@/hooks/useAppSettings";

const AppSettingsPage = () => {
  const {
    featureFlags,
    appSettings,
    isLoading,
    saveSetting,
    toggleFeatureFlag,
    refetchSettings,
  } = useAppSettings();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refetchSettings();
      toast.success("Settings refreshed");
    } catch (error) {
      toast.error("Failed to refresh settings");
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <AdminLayout
      title="Application Settings"
      description="General settings, email templates, and feature flags are grouped in one calm control surface with the active area always visible."
      actions={
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="rounded-lg border-gray-200 text-gray-700 hover:border-whatsapp hover:text-whatsapp"
        >
          <RefreshCw size={16} className={isRefreshing ? "animate-spin" : ""} />
          {isRefreshing ? "Refreshing..." : "Refresh"}
        </Button>
      }
    >
      <Tabs defaultValue="settings" className="space-y-4">
        <TabsList className="h-auto flex-wrap justify-start gap-2 rounded-lg border border-gray-200 bg-white p-2 shadow-sm">
          <TabsTrigger
            value="settings"
            className="rounded-lg px-4 py-2.5 data-[state=active]:bg-whatsapp data-[state=active]:text-white data-[state=active]:shadow-none"
          >
            <Settings size={16} />
            General settings
          </TabsTrigger>
          <TabsTrigger
            value="email-templates"
            className="rounded-lg px-4 py-2.5 data-[state=active]:bg-whatsapp data-[state=active]:text-white data-[state=active]:shadow-none"
          >
            <Mail size={16} />
            Email templates
          </TabsTrigger>
          <TabsTrigger
            value="features"
            className="rounded-lg px-4 py-2.5 data-[state=active]:bg-whatsapp data-[state=active]:text-white data-[state=active]:shadow-none"
          >
            <Flag size={16} />
            Feature flags
          </TabsTrigger>
        </TabsList>

        <TabsContent value="settings">
          <AppSettingsTable
            settings={appSettings}
            isLoading={isLoading}
            onSaveSetting={saveSetting}
          />
        </TabsContent>

        <TabsContent value="email-templates">
          <EmailTemplateAccordion
            settings={appSettings}
            onSaveSetting={saveSetting}
          />
        </TabsContent>

        <TabsContent value="features">
          <FeatureFlagTable
            featureFlags={featureFlags}
            isLoading={isLoading}
            onToggleFeatureFlag={toggleFeatureFlag}
          />
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
};

export default AppSettingsPage;
